import axios from 'axios';

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add variables to prevent continuous refresh calls
let isRefreshing = false;
let refreshSubscribers = [];

// Helper function to handle queued requests
const onRefreshed = (token) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
};

// Helper function to handle logout and navigation
const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    // Set session storage to show toast message (optional)
    if (!sessionStorage.getItem("showLoginToast")) {
        sessionStorage.setItem("showLoginToast", "You have been logged out, Please try login again");
    }
    
    // Navigate to login page
    window.location.href = '/login';
};

axiosInstance.interceptors.request.use(
    (config) => {
        const accessToken = localStorage.getItem('accessToken');
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
            delete config.headers.Authorization;
        }
        // Make sure not to set 'Content-Type' if sending FormData
        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = 'multipart/form-data';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Improved refresh token interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            // If already refreshing, queue the request
            if (isRefreshing) {
                return new Promise((resolve) => {
                    refreshSubscribers.push((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        resolve(axiosInstance(originalRequest));
                    });
                });
            }
            
            originalRequest._retry = true;
            isRefreshing = true;
            
            try {
                const newToken = await refreshAccessToken();
                
                // Update the Authorization header
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                
                // Notify all queued requests
                onRefreshed(newToken);
                
                // Retry the original request
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                
                // Handle logout and navigation on refresh failure
                handleLogout();
                
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false; // Reset flag
            }
        }
        
        return Promise.reject(error);
    }
);

const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }
    
    try {
        // Remove old token before refreshing
        localStorage.removeItem('accessToken');
        
        const req_body = { refresh: refreshToken };
        if (localStorage.getItem('preface_client_fcm_token') !== null) {
            req_body.fcm_token = localStorage.getItem('preface_client_fcm_token');
        }
        
        // Use a separate axios instance for refresh to avoid interceptor loops
        const refreshResponse = await axios.post(`${process.env.REACT_APP_API_URL}${process.env.REACT_APP_REFRESH_TOKEN_URL}`, req_body);
        const { token, refresh } = refreshResponse.data.data;
        
        if (!token || !refresh) {
            throw new Error('Invalid token response');
        }
        
        localStorage.setItem("accessToken", token);
        localStorage.setItem("refreshToken", refresh);
        
        return token;
    } catch (error) {
        console.error('Refresh token error:', error);
        
        // If refresh fails, it means refresh token is also expired/invalid
        throw error;
    }
};

export default axiosInstance;